<script setup lang="ts">
import dayjs from "dayjs";
import { Circle } from "lucide-vue-next";

import {
	approveDeleteRequest,
	createDeleteRequest,
	revertDeleteRequest,
} from "~/api/user";
import VModal from "~/components/common/VModal.vue";

type Props = {
	open: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits(["close"]);

const removeReasons: Array<string> = [
	"Не нашел нужных функций",
	"Трудности в использовании",
	"Слишком дорого",
	"Завершил учебу",
	"Нашел альтернативный сервис",
	"Другое",
];

const userStore = useUserStore();
const authStore = useAuthStore();

const selectedReason = ref<string | undefined>();
const reasonDescription = ref<string>("");
const isLoading = ref<boolean>(false);

const step = ref<1 | 2>(1);

const onClose = () => {
	emit("close");
};

const onSubmitReason = async () => {
	isLoading.value = true;
	const reason =
		selectedReason.value +
		(reasonDescription.value.length > 5 ? ` (${reasonDescription.value})` : "");
	await createDeleteRequest({ reason });
	await onApprove(); // force approving a delete request
	isLoading.value = false;
	step.value = 2;
};

const onApprove = async () => {
	isLoading.value = true;
	await approveDeleteRequest();
	isLoading.value = false;
	authStore.logout();
	navigateTo("/");
};

const onRevert = async () => {
	isLoading.value = true;
	await revertDeleteRequest();
	await userStore.fetchUser();
	step.value = 1;
	isLoading.value = false;
	onClose();
};

watch(
	() => userStore.user?.delete_request?.approved,
	(isApproved) => {
		if (isApproved) step.value = 2;
	},
	{ immediate: true },
);
</script>

<template>
  <v-modal :open="props.open">
    <div
      v-if="step === 1"
      class="delete-request"
    >
      <h1>Нам жаль, что вы уходите!</h1>
      <div class="delete-request__body">
        <p>Поделитесь, пожалуйста, причиной удаления профиля. Ваш отзыв поможет нам стать лучше</p>
        <ul>
          <li
            v-for="reason in removeReasons"
            :key="reason"
            :class="{ selected: reason === selectedReason }"
            @click="selectedReason = reason"
          >
            <Circle
              v-if="reason === selectedReason"
              width="18"
              height="18"
            />
            <Circle
              v-else
              width="18"
              height="18"
            />
            <span>{{ reason }}</span>
          </li>
        </ul>
        <v-textarea
          v-model="reasonDescription"
          class="w-full"
          placeholder="Расскажите подробнее"
          :disabled="isLoading"
          :rows='3'
          :max-rows='10'
        />
      </div>
      <div class="delete-request__actions">
        <v-button
          :loading="isLoading"
          @click="onClose"
        >
          Отменить
        </v-button>
        <v-button
          schema="outline"
          :disabled="!selectedReason"
          :loading="isLoading"
          @click="onSubmitReason"
        >
          Продолжить
        </v-button>
      </div>
    </div>
    <div
      v-else-if="step === 2"
      class="delete-request"
    >
      <h1>Запрос на удаление аккаунта отправлен</h1>
      <div class="delete-request__body">
        <p>
          Ваш аккаунт и все данные, связанные с ним, будут удалены через {{ dayjs(userStore.user?.delete_request?.approved_at).add(30, 'days').diff(dayjs(), 'days') }} дней.
        </p>
        <p class="muted">
          Чтобы продолжить использовать сервис, Вы можете восстановить свой аккаунт.
        </p>
      </div>
      <div class="delete-request__actions">
        <v-button
          :loading="isLoading"
          @click="onRevert"
        >
          Восстановить аккаунт 
        </v-button>
      </div>
    </div>
  </v-modal>
</template>

<style scoped lang="scss">
.delete-request {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  > h1 {
    font-size: 22px;
    line-height: 28px;
    margin: 0;
  }

  .delete-request__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;

    > * {
      width: 100%;
    }
  }

  .delete-request__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin: 8px 0;

    > p {
      font-size: 14px;
      line-height: 20px;

      &.muted {
        color: $foreground-gray;
      }
    }

    > ul {
      display: flex;
      flex-direction: column;
      align-items: start;
      width: 100%;

      > li {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 4px 0;

        &:hover {
          cursor: pointer;
        }

        &::after {
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 100%;
          position: absolute;
          display: flex;
          background: white;
          left: 6px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          opacity: 0;
          transition: opacity 200ms;
        }

        &.selected {
          &::after {
            opacity: 1;
          }

          > svg {
            fill: $background-theme;
            stroke: none;
          }
        }
      }
    }

    .delete-request__body__promocode {
      padding: 12px 16px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border-radius: 12px;
      background: $background-gray;

      &:hover {
        cursor: pointer;
      }
    }
  }
}
</style>
